import { useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { theme } from 'aimo-ui';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { useAimoPlusEligibility } from 'hooks/useAimoPlusEligibility';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import usePollSalesOrderLastRecurringPaymentStatus from 'hooks/usePollSalesOrderLastRecurringPaymentStatus';
import { getLanguage } from 'i18n/init';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { featureCountryCode } from 'states/persistInStorage';
import { CountryCode, Lang } from 'types/generatedSchemaTypes';
import { AccountNavProps } from 'types/navigation';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { languageLabels } from 'utils/languageUtils';
import { MenuItemWrapped, MenuSection } from './MenuComponents';
import SendEmailReceiptsMenuItem from './SendEmailReceiptsToggleItem';
import { useUiStore } from 'stores/uiStore';

const AccountMenu = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<AccountNavProps>();
  const {
    flags: { account_notification_settings, company_benefits },
  } = useContext(FeatureFlagsContext);
  const showAccountNotificationSettings = showFeatureFlag(
    account_notification_settings
  );
  const {
    customer: { companyBenefits, aimoPlusSubscriber },
    customerSubscription,
  } = useContext(AccountContext);

  const { isFailed: isSubscriptionPaymentFailed } =
    usePollSalesOrderLastRecurringPaymentStatus(
      { salesOrderUid: customerSubscription?.uid ?? '' },
      !customerSubscription?.uid
    );
  const hasCompanybenefits = (companyBenefits?.length ?? 0) > 0;
  const showCompanyBenefits = showFeatureFlag(company_benefits);
  const appCountryCode = useReactiveVar(featureCountryCode) as CountryCode;
  const { isLoggedIn } = useIsLoggedIn();
  const { isEligible: isAimoPlusEligible } = useAimoPlusEligibility({
    isLoggedIn,
    aimoPlusSubscriber: !!aimoPlusSubscriber,
  });
  const showAimoSubscriptionMenu = !!aimoPlusSubscriber || isAimoPlusEligible;

  return (
    <MenuSection noDivider headline={t('account.account')}>
      <SendEmailReceiptsMenuItem />

      <MenuItemWrapped
        menuLabel={t('account.tagsAndCards.title')}
        iconName="Mifare"
        testID="mifare-card"
        onPress={() => navigate('mifareAndRfid')}
        withArrow={true}
      />

      <MenuItemWrapped
        menuLabel={t('language.title')}
        iconName="Globe-Outlined"
        testID="language-link"
        subtitle={languageLabels[getLanguage() as Lang]}
        onPress={() => useUiStore.getState().setSheetModal('language')}
      />
      <MenuItemWrapped
        menuLabel={t('account.appLocation')}
        iconName="Pin-Outlined"
        testID="app-location"
        onPress={() => useUiStore.getState().setSheetModal('appLocation')}
        subtitle={t(`account.appLocations.${appCountryCode}`)}
      />
      {showAccountNotificationSettings && (
        <MenuItemWrapped
          menuLabel={t('account.notificationSettings')}
          iconName="Notification-Outlined"
          testID="notifications-link"
          onPress={() => navigate('notifications')}
        />
      )}
      {showAimoSubscriptionMenu && (
        <MenuItemWrapped
          menuLabel={t('account.aimoSubscription.title')}
          iconName="Aimo-Plus"
          testID="aimo-subscription-link"
          onPress={() => navigate('aimoSubscription')}
          indicatorType={isSubscriptionPaymentFailed ? 'error' : undefined}
          indicatorNumber={isSubscriptionPaymentFailed ? 1 : undefined}
          subtitle={
            aimoPlusSubscriber ? '' : t('account.aimoSubscription.description')
          }
          pillText={
            aimoPlusSubscriber
              ? t('account.aimoSubscription.active')
              : undefined
          }
          pillConfig={{
            bg: theme.colors.aimoPlus,
            py: 5,
            mr: 15,
          }}
        />
      )}
      {showCompanyBenefits && hasCompanybenefits && (
        <MenuItemWrapped
          menuLabel={t('account.companyPermit')}
          iconName="Business-Outlined"
          testID="corporate-permits-link"
          subtitle={
            (companyBenefits?.[0]?.name as string) ||
            t('account.companyPermits.noPermit')
          }
          onPress={() => navigate('companyPermit')}
          withArrow
        />
      )}
      <MenuItemWrapped
        menuLabel={t('account.contactInfo')}
        iconName="User-Outlined"
        testID="contact-info-link"
        onPress={() => navigate('contactInfo')}
      />
      <MenuItemWrapped
        menuLabel={t('account.terms.privacyTermsAndConditions')}
        iconName="Document-Outlined"
        testID="privacy-terms-conditions-link"
        onPress={() => navigate('viewTerms')}
      />
      <MenuItemWrapped
        testID="logout-button"
        menuLabel={t('account.signOut')}
        onPress={() => useUiStore.getState().setSheetModal('signOut')}
        iconName="Sign_Out"
        noBorder
      />
    </MenuSection>
  );
};

export default AccountMenu;
